/**
 * PublicShell — bố cục chung cho mọi trang public (không cần đăng nhập).
 *
 * YC13 (2026-05-23): áp dụng template Landing (TopBar + Footer) lên
 * `/public/results` và các trang public khác để bố cục thống nhất.
 *
 * Dùng TopBar luôn ở chế độ "scrolled" (nền trắng, không transparent) vì các
 * trang nội dung không có Hero full-bleed như Landing.
 */
import { useEffect, useState } from 'react'
import { Link as RouterLink, useLocation } from 'react-router-dom'
import {
  AppBar,
  Box,
  Button,
  Container,
  Divider,
  IconButton,
  Stack,
  Toolbar,
  Typography,
  useMediaQuery,
  useTheme,
} from '@mui/material'
import { Login, Menu as MenuIcon, Close as CloseIcon } from '@mui/icons-material'
import { useTranslation } from 'react-i18next'
import { brand } from '../theme/brand'
import { siteContent } from '../config/siteContent'
import { useAppSelector } from '../hooks/redux'
import LanguageSwitcher from './LanguageSwitcher'

const INK = brand.navy
const TEXT_BODY = '#2A3548'
const HAIRLINE = 'rgba(20,78,140,0.12)'

function PublicTopBar() {
  const theme = useTheme()
  const isMobile = useMediaQuery(theme.breakpoints.down('lg'))
  const [open, setOpen] = useState(false)
  const location = useLocation()
  const { isAuthenticated } = useAppSelector((state) => state.auth)
  const { t } = useTranslation()

  const NAV_LINKS = [
    { label: t('nav.home'), href: '/#hero' },
    { label: t('nav.about'), href: '/#overview' },
    { label: t('nav.timeline'), href: '/#timeline' },
    { label: t('nav.results'), href: '/public/results' },
    { label: t('nav.rules'), href: '/#rules' },
    { label: t('nav.faq'), href: '/#faq' },
    { label: t('nav.contact'), href: '/#contact' },
  ]

  return (
    <>
      <AppBar
        position="fixed"
        elevation={0}
        sx={{
          background: 'rgba(255,255,255,0.96)',
          backdropFilter: 'blur(14px)',
          borderBottom: `1px solid ${HAIRLINE}`,
          color: INK,
        }}
      >
        <Toolbar sx={{ maxWidth: 1280, width: '100%', mx: 'auto', minHeight: { xs: 60, md: 72 }, gap: 1 }}>
          <Stack
            direction="row"
            alignItems="center"
            spacing={1.2}
            sx={{ minWidth: 0, flexShrink: 1, textDecoration: 'none', color: 'inherit' }}
            component={RouterLink}
            to="/"
          >
            <Box
              component="img"
              src="/moot-court-navbar-logo.jpg"
              alt={siteContent.brand.short}
              sx={{
                width: 40,
                height: 40,
                objectFit: 'contain',
                flexShrink: 0,
                filter: 'drop-shadow(0 2px 6px rgba(20,78,140,0.2))',
              }}
            />
            <Box sx={{ minWidth: 0 }}>
              <Typography
                noWrap
                sx={{
                  fontWeight: 800,
                  fontSize: { xs: '0.9rem', md: '0.95rem' },
                  letterSpacing: 0.5,
                  lineHeight: 1.1,
                  color: INK,
                }}
              >
                {siteContent.brand.short}
              </Typography>
              <Typography
                noWrap
                sx={{
                  fontFamily: '"Roboto", "Helvetica Neue", Helvetica, Arial, sans-serif',
                  color: brand.hotPink,
                  fontSize: '0.66rem',
                  letterSpacing: 2,
                  lineHeight: 1,
                  fontWeight: 700,
                }}
              >
                {siteContent.brand.season}
              </Typography>
            </Box>
          </Stack>

          {!isMobile ? (
            <Stack
              direction="row"
              spacing={0.25}
              sx={{ flexGrow: 1, justifyContent: 'center' }}
            >
              {NAV_LINKS.map((link) => {
                const isActive =
                  link.href === location.pathname ||
                  (link.href.startsWith('/public/') && location.pathname.startsWith(link.href))
                return (
                  <Button
                    key={link.href}
                    href={link.href}
                    sx={{
                      color: isActive ? INK : TEXT_BODY,
                      fontWeight: isActive ? 700 : 500,
                      fontSize: '0.82rem',
                      textTransform: 'none',
                      px: 1.1,
                      minWidth: 0,
                      whiteSpace: 'nowrap',
                      '&:hover': { color: INK, background: 'rgba(20,78,140,0.06)' },
                    }}
                  >
                    {link.label}
                  </Button>
                )
              })}
            </Stack>
          ) : (
            <Box sx={{ flexGrow: 1, display: 'flex', justifyContent: 'flex-end' }}>
              <IconButton onClick={() => setOpen((v) => !v)} sx={{ color: INK }}>
                {open ? <CloseIcon /> : <MenuIcon />}
              </IconButton>
            </Box>
          )}

          <Box sx={{ mr: 1, flexShrink: 0 }}>
            <LanguageSwitcher />
          </Box>

          <Button
            component={RouterLink}
            to={isAuthenticated ? '/dashboard' : '/login'}
            variant="contained"
            size="small"
            startIcon={<Login />}
            sx={{
              background: brand.hotPink,
              color: 'common.white',
              fontWeight: 700,
              textTransform: 'none',
              whiteSpace: 'nowrap',
              flexShrink: 0,
              boxShadow: '0 6px 18px rgba(246,65,131,0.3)',
              '&:hover': { background: '#d9356f' },
            }}
          >
            {isAuthenticated ? t('topbar.dashboard') : t('topbar.login')}
          </Button>
        </Toolbar>
      </AppBar>

      {isMobile && open && (
        <Box
          sx={{
            position: 'fixed',
            top: 60,
            left: 0,
            right: 0,
            zIndex: 1200,
            background: 'rgba(255,255,255,0.98)',
            backdropFilter: 'blur(14px)',
            borderBottom: `1px solid ${HAIRLINE}`,
            py: 2,
          }}
        >
          <Stack>
            {NAV_LINKS.map((link) => (
              <Button
                key={link.href}
                href={link.href}
                onClick={() => setOpen(false)}
                sx={{
                  color: TEXT_BODY,
                  fontWeight: 500,
                  fontSize: '0.95rem',
                  textTransform: 'none',
                  justifyContent: 'flex-start',
                  px: 3,
                  py: 1.2,
                  borderRadius: 0,
                  '&:hover': { color: INK, background: 'rgba(20,78,140,0.05)' },
                }}
              >
                {link.label}
              </Button>
            ))}
          </Stack>
        </Box>
      )}
    </>
  )
}

function PublicFooter() {
  const { footer } = siteContent

  return (
    <Box sx={{ py: 5, background: INK, color: 'common.white' }}>
      <Container maxWidth="lg">
        <Stack
          direction={{ xs: 'column', md: 'row' }}
          spacing={2.5}
          alignItems="center"
          justifyContent="space-between"
        >
          <Stack direction="row" alignItems="center" spacing={1.5}>
            <Box
              sx={{
                width: 38,
                height: 38,
                borderRadius: 1.2,
                background: 'rgba(255,255,255,0.96)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                p: 0.4,
              }}
            >
              <Box
                component="img"
                src="/logo.png"
                alt={siteContent.brand.short}
                sx={{ width: '100%', height: '100%', objectFit: 'contain' }}
              />
            </Box>
            <Box>
              <Typography
                sx={{
                  color: 'common.white',
                  fontWeight: 800,
                  fontSize: '0.95rem',
                  textTransform: 'uppercase',
                  letterSpacing: 0.5,
                }}
              >
                {siteContent.brand.short}
              </Typography>
              <Typography sx={{ color: 'rgba(255,255,255,0.7)', fontSize: '0.78rem' }}>
                {footer.organizer}
              </Typography>
            </Box>
          </Stack>
          <Divider
            sx={{ width: { xs: 60, md: 'auto' }, height: { md: 28 }, borderColor: 'rgba(255,255,255,0.2)' }}
            orientation={{ xs: 'horizontal', md: 'vertical' } as any}
          />
          <Typography
            sx={{
              color: 'rgba(255,255,255,0.6)',
              fontFamily: '"Roboto", "Helvetica Neue", Helvetica, Arial, sans-serif',
              fontSize: '0.78rem',
              letterSpacing: 1,
            }}
          >
            © {footer.copyright}
          </Typography>
        </Stack>
      </Container>
    </Box>
  )
}

export default function PublicShell({ children }: { children: React.ReactNode }) {
  // Tự scroll lên top khi đổi route — content section bắt đầu sau AppBar.
  const location = useLocation()
  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'auto' })
  }, [location.pathname])

  return (
    <Box sx={{ minHeight: '100dvh', background: '#FFFFFF', display: 'flex', flexDirection: 'column' }}>
      <PublicTopBar />
      <Box component="main" sx={{ pt: { xs: '60px', md: '72px' }, flex: 1 }}>
        {children}
      </Box>
      <PublicFooter />
    </Box>
  )
}
